// Forgot-password screen — minimal W2.0 surface. Always returns accepted;
// shows a confirmation banner whether the email exists or not.

import { useState } from "react";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api/auth";
import { api } from "@/lib/api/client";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrorMsg(null);
    try {
      const res = await api.POST("/api/v1/auth/forgot-password", {
        body: { email },
      });
      if (!res.response.ok) {
        throw new ApiError(res.response.status, "AUTH_FORGOT_FAILED", "送出失敗");
      }
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg("請檢查網絡後再試一次");
      }
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell title="重置密碼">
        <div className="flex flex-col gap-3 text-body text-ink-2">
          <p>
            如果 <strong className="text-ink-1">{email}</strong> 已綁定 heshe 帳戶,
            我們已將重置連結寄出。請於一小時內點擊信件中的連結。
          </p>
          <p className="text-body-sm text-ink-3">沒有收到? 檢查垃圾信件夾, 或重新提交一次。</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="忘記密碼"
      subtitle="輸入註冊時使用的電子郵件, 我們會把重置連結寄給你。"
      backHref="/login"
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <FormErrorBanner message={errorMsg} />
        <FormField
          name="email"
          label="電子郵件"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={setEmail}
          autoFocus
          disabled={busy}
        />
        <Button
          type="submit"
          variant="primary"
          disabled={busy}
          data-testid="submit-forgot"
          leadingIcon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        >
          {busy ? "送出中…" : "寄出重置連結"}
        </Button>
      </form>
    </AuthShell>
  );
}
